import { Option } from "@inrev/core";
import { useCallback, useId, useMemo, useState } from "react";
import type { SideDrawerProps } from "./SideDrawer";

export type SideDrawerTriggerProps = {
	onClick: () => void;
	"aria-controls": string;
	isOpen: boolean;
	testId?: string;
	trackingId?: string;
};

export type SideDrawerControlProps = Pick<
	SideDrawerProps,
	"id" | "isOpen" | "onClose" | "testId" | "trackingId"
>;

type UseSideDrawerProps = {
	defaultOpen?: boolean;
	testId?: string;
	trackingId?: string;
};

export const useSideDrawer = (options?: UseSideDrawerProps) => {
	const [isOpen, setIsOpen] = useState(options?.defaultOpen ?? false);
	const id = useId();

	const open = useCallback(() => setIsOpen(true), []);
	const close = useCallback(() => setIsOpen(false), []);
	const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

	const triggerProps = useMemo(
		(): SideDrawerTriggerProps => ({
			onClick: toggle,
			"aria-controls": id,
			isOpen,
			...Option.map(options?.testId, (prefix) => ({ testId: `${prefix}-trigger` })),
			...Option.map(options?.trackingId, (prefix) => ({ trackingId: `${prefix}-trigger` })),
		}),
		[toggle, id, isOpen, options?.testId, options?.trackingId],
	);

	const drawerProps = useMemo(
		(): SideDrawerControlProps => ({
			id,
			isOpen,
			onClose: close,
			...Option.map(options?.testId, (prefix) => ({ testId: `${prefix}-drawer` })),
			...Option.map(options?.trackingId, (prefix) => ({ trackingId: `${prefix}-drawer` })),
		}),
		[id, isOpen, close, options?.testId, options?.trackingId],
	);

	return { isOpen, open, close, toggle, triggerProps, drawerProps };
};
